import { useEffect, useRef } from 'react';
import { X, CheckCircle2, AlertCircle, AlertTriangle } from 'lucide-react';
import { BroadcastResult, DomainHealth, BroadcastRevenue } from '@/services/api/meta-analytics';
import { fmtMoney, fmtN, fmtPct, STATUS_LABEL, STATUS_COLOR } from './utils';

// ── helpers ───────────────────────────────────────────────────────────────────

function Skeleton({ className = '' }: { className?: string }) {
    return <div className={`animate-pulse bg-gray-100 rounded ${className}`} />;
}

function ecpmColorClass(ecpm: number): string {
    if (ecpm > 100) return 'text-emerald-700 font-bold';
    if (ecpm >= 50) return 'text-emerald-600 font-semibold';
    if (ecpm > 0) return 'text-amber-600 font-semibold';
    return 'text-gray-400';
}

// ── Props ─────────────────────────────────────────────────────────────────────

export interface DomainPanelProps {
    domain: string;
    domainHealth: DomainHealth | undefined;
    /** revenue data deste domain a partir do grupo que abriu o painel (pode ser undefined) */
    revenueByDomain?: Record<string, BroadcastRevenue | null>;
    allGroups: BroadcastResult[];
    /** map groupId → revenue total para este domain */
    groupRevenueMap: Record<number, number | undefined>;
    onClose: () => void;
    onOpenGroup: (groupId: number, groupTitle: string, groupStatus: string) => void;
}

// ── Componente ────────────────────────────────────────────────────────────────

export function DomainPanel({
    domain,
    domainHealth,
    allGroups,
    groupRevenueMap,
    onClose,
    onOpenGroup,
}: DomainPanelProps) {
    const panelRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [onClose]);

    const hasRevenue = domainHealth?.has_revenue ?? false;

    // grupos que usam este domain (têm summary com pages_by_domain contendo este domain)
    // No contexto actual, filtramos pelos grupos cujo revenue contém este domain
    // Usamos groupRevenueMap para identificar grupos relacionados
    const relatedGroups = allGroups.filter(g => groupRevenueMap[g.id] !== undefined);

    const ecpm = domainHealth?.ecpm ?? 0;

    return (
        <div
            className="fixed inset-0 z-60 flex justify-end bg-black/50"
            style={{ zIndex: 60 }}
            onClick={e => {
                if (panelRef.current && !panelRef.current.contains(e.target as Node)) onClose();
            }}
            role="dialog"
            aria-modal="true"
        >
            <div
                ref={panelRef}
                className="relative h-full w-full max-w-2xl bg-white shadow-2xl overflow-y-auto flex flex-col"
            >
                {/* Header */}
                <div className="sticky top-0 z-10 bg-white border-b border-gray-100 px-6 py-4">
                    <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                                <h2 className="font-bold text-gray-900 text-lg truncate" title={domain}>
                                    {domain}
                                </h2>
                                {hasRevenue ? (
                                    <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium bg-emerald-100 text-emerald-700">
                                        <CheckCircle2 className="w-3 h-3" /> Com revenue
                                    </span>
                                ) : (
                                    <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium bg-red-100 text-red-600">
                                        <AlertCircle className="w-3 h-3" /> Sem revenue
                                    </span>
                                )}
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 rounded-lg border border-gray-100 text-gray-400 hover:text-gray-600 hover:bg-gray-50 transition-colors shrink-0"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                </div>

                <div className="flex-1 px-6 py-6 space-y-8">

                    {/* Banner sem revenue */}
                    {!hasRevenue && (
                        <div className="rounded-xl bg-red-50 border border-red-200 px-4 py-4 flex gap-3">
                            <AlertTriangle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                            <div>
                                <p className="text-sm font-semibold text-red-800">
                                    Este domain não possui dados de revenue no key-values
                                </p>
                                <p className="text-xs text-red-600 mt-0.5">
                                    Verifique se o domain está configurado na plataforma de ads
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Stats básicas — sempre visíveis */}
                    {domainHealth ? (
                        <section>
                            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                                Visão geral
                            </h3>
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                                <div className="rounded-xl bg-gray-50 p-3">
                                    <p className="text-xs text-gray-400 mb-0.5">Páginas</p>
                                    <p className="text-xl font-bold text-gray-900">{domainHealth.total_pages}</p>
                                </div>
                                <div className="rounded-xl bg-gray-50 p-3">
                                    <p className="text-xs text-gray-400 mb-0.5">Grupos</p>
                                    <p className="text-xl font-bold text-gray-900">{domainHealth.total_groups}</p>
                                </div>
                                <div className="rounded-xl bg-gray-50 p-3">
                                    <p className="text-xs text-gray-400 mb-0.5">Enviados</p>
                                    <p className="text-xl font-bold text-gray-900">{fmtN(domainHealth.total_sent)}</p>
                                </div>
                                <div className="rounded-xl bg-gray-50 p-3">
                                    <p className="text-xs text-gray-400 mb-0.5">Falhas</p>
                                    <p className={`text-xl font-bold ${domainHealth.failure_rate > 50 ? 'text-red-600' : 'text-gray-900'}`}>
                                        {fmtPct(domainHealth.failure_rate)}
                                    </p>
                                </div>
                                {hasRevenue && (
                                    <>
                                        <div className="rounded-xl bg-gray-50 p-3">
                                            <p className="text-xs text-gray-400 mb-0.5">Impressões</p>
                                            <p className="text-xl font-bold text-gray-900">{fmtN(domainHealth.impressions)}</p>
                                        </div>
                                        <div className="rounded-xl bg-gray-50 p-3">
                                            <p className="text-xs text-gray-400 mb-0.5">Ad Clicks</p>
                                            <p className="text-xl font-bold text-gray-900">{fmtN(domainHealth.ad_clicks)}</p>
                                        </div>
                                    </>
                                )}
                            </div>
                        </section>
                    ) : (
                        <div className="grid grid-cols-3 gap-3">
                            {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-16" />)}
                        </div>
                    )}

                    {/* KPIs de revenue */}
                    {hasRevenue && domainHealth && (
                        <section>
                            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                                Revenue
                            </h3>
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                                <div className="rounded-xl bg-emerald-50 p-3">
                                    <p className="text-xs text-gray-400 mb-0.5">Revenue total</p>
                                    <p className="text-xl font-bold text-emerald-700">{fmtMoney(domainHealth.revenue)}</p>
                                </div>
                                <div className="rounded-xl bg-gray-50 p-3">
                                    <p className="text-xs text-gray-400 mb-0.5">eCPM</p>
                                    <p className={`text-xl ${ecpmColorClass(ecpm)}`}>{fmtMoney(ecpm)}</p>
                                </div>
                                <div className="rounded-xl bg-gray-50 p-3">
                                    <p className="text-xs text-gray-400 mb-0.5">Impressões</p>
                                    <p className="text-xl font-bold text-gray-900">{fmtN(domainHealth.impressions)}</p>
                                </div>
                            </div>

                            {/* Barra de performance eCPM */}
                            <div className="mt-3 rounded-xl border border-gray-100 px-4 py-3">
                                <div className="flex items-center justify-between mb-1.5">
                                    <span className="text-xs text-gray-500">Performance eCPM</span>
                                    <span className={`text-xs font-semibold ${ecpmColorClass(ecpm)}`}>
                                        {ecpm > 100 ? 'Excelente' : ecpm >= 50 ? 'Bom' : ecpm > 0 ? 'Fraco' : 'Sem dados'}
                                    </span>
                                </div>
                                <div className="w-full bg-gray-100 rounded-full h-2">
                                    <div
                                        className={`h-2 rounded-full transition-all ${
                                            ecpm > 100 ? 'bg-emerald-600' : ecpm >= 50 ? 'bg-emerald-400' : 'bg-amber-400'
                                        }`}
                                        style={{ width: `${Math.min((ecpm / 150) * 100, 100)}%` }}
                                    />
                                </div>
                                <div className="flex justify-between mt-1">
                                    <span className="text-xs text-gray-300">$0</span>
                                    <span className="text-xs text-amber-400">$50</span>
                                    <span className="text-xs text-emerald-400">$100</span>
                                    <span className="text-xs text-emerald-600">$150+</span>
                                </div>
                            </div>
                        </section>
                    )}

                    {/* Grupos que usam este domain */}
                    {relatedGroups.length > 0 && (
                        <section>
                            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                                Grupos com este domain ({relatedGroups.length})
                            </h3>
                            <div className="divide-y divide-gray-100 rounded-xl border border-gray-100 overflow-hidden">
                                {relatedGroups.slice(0, 20).map(g => {
                                    const rev = groupRevenueMap[g.id];
                                    const statusCls = STATUS_COLOR[g.status] ?? 'bg-gray-100 text-gray-500';
                                    return (
                                        <button
                                            key={g.id}
                                            className="w-full flex items-center gap-3 px-4 py-3 bg-white hover:bg-gray-50 transition-colors text-left"
                                            onClick={() => onOpenGroup(g.id, g.title, g.status)}
                                        >
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium text-gray-800 truncate" title={g.title}>
                                                    {g.title}
                                                </p>
                                                <p className="text-xs text-gray-400 mt-0.5">
                                                    {fmtN(g.total_sent)} enviados
                                                </p>
                                            </div>
                                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium shrink-0 ${statusCls}`}>
                                                {STATUS_LABEL[g.status] ?? g.status}
                                            </span>
                                            {rev !== undefined ? (
                                                <span className="text-sm font-semibold text-emerald-700 shrink-0">
                                                    {fmtMoney(rev)}
                                                </span>
                                            ) : (
                                                <span className="text-xs text-gray-300 shrink-0">—</span>
                                            )}
                                            <span className="text-xs text-gray-300 shrink-0">→</span>
                                        </button>
                                    );
                                })}
                            </div>
                        </section>
                    )}

                    {/* Páginas — informativo */}
                    {domainHealth && (
                        <section>
                            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                                Páginas
                            </h3>
                            <p className="text-sm text-gray-500">
                                {domainHealth.total_pages} página{domainHealth.total_pages !== 1 ? 's' : ''} associada{domainHealth.total_pages !== 1 ? 's' : ''} a este domain.
                            </p>
                            <p className="text-xs text-gray-400 mt-1">
                                Para ver páginas individuais, acesse o detalhe do grupo correspondente.
                            </p>
                        </section>
                    )}
                </div>
            </div>
        </div>
    );
}
